import type { FiuuTopUpErrorKind } from './fiuu-topup-api.js';
import { toFiuuAmountParam, toFiuuTopUpErrorKind } from './fiuu-topup-api.js';
import type { LegacyApiClient } from './legacy-api/client.js';
import type { RmTopUpStart } from './rm-topup.js';
import { normaliseRmTopUpStart } from './rm-topup.js';

/**
 * Starting an e-wallet top-up (MYP-135): `rm_pay`, from the vendor's guide of 2026-11-03. It
 * reads the answer through `normaliseRmTopUpStart`, so what comes out is either a checked link to
 * RM's checkout page or an `RmTopUpError` — never a raw answer, and never a link nobody has checked.
 *
 * How the top-up ended is read from `get_trade`, as for FPX (`findFiuuTopUpState`): an e-wallet
 * top-up is an `accdetail` like any other.
 */

/**
 * `rm_pay`'s result code (or the client's own) → the kind, in the same words as FPX so the screen
 * says the same thing for the same trouble. `rm_pay`'s codes match `molpay_pay`'s, plus `-4`:
 * RM refused to create the order (the vendor's RM set-up) — nothing the customer can fix, so
 * "try later", like the vendor's own database error (`0`).
 */
export function toRmTopUpErrorKind(code: string): FiuuTopUpErrorKind {
  return code === '-4' ? 'unavailable' : toFiuuTopUpErrorKind(code);
}

/** Starting an e-wallet top-up ended without a usable answer. `message` is for the log, not the screen. */
export class RmTopUpError extends Error {
  constructor(
    readonly kind: FiuuTopUpErrorKind,
    readonly code: string,
    message: string,
  ) {
    super(`rm_pay failed (${code}, ${kind}): ${message}`);
    this.name = 'RmTopUpError';
  }
}

/**
 * Start an e-wallet top-up of `amountCents`: the vendor records it, orders it from RM and answers
 * with RM's checkout link. Resolves only to an answer the normaliser accepted — the link is RM's
 * checkout page and the amount is the one asked for.
 *
 * Every call records a new top-up waiting for payment (and a new RM order), so the caller must not
 * call it twice for one press of Pay. `remark` is optional in the guide and not sent.
 */
export async function startRmTopUp(
  client: LegacyApiClient,
  amountCents: number,
): Promise<RmTopUpStart> {
  const amount = toFiuuAmountParam(amountCents);
  const result = await client.call<unknown>('rm_pay', { amount });
  if (!result.ok) {
    throw new RmTopUpError(toRmTopUpErrorKind(result.code), result.code, result.message);
  }

  const start = normaliseRmTopUpStart(result.data, amountCents);
  if (start === undefined) {
    throw new RmTopUpError(
      'unusable',
      'UNUSABLE',
      "The answer had no accdetailid, a different amount, or a link that is not RM's checkout page",
    );
  }
  return start;
}
